import React, { useEffect, useRef, useState } from 'react'
import { motion, useMotionValue, animate } from "motion/react"
import { FaChevronLeft, FaChevronRight } from "react-icons/fa"
import { useReducedMotionSafe } from "../../lib/motion"

// Apple's momentum-projection formula (see `Designing Fluid Interfaces`, WWDC 2018):
// projects where a flick would coast to a stop, so the carousel snaps to the
// slide the gesture was heading toward rather than the one nearest the release point.
function project(velocity, decelerationRate = 0.998) {
  return (velocity / 1000) * decelerationRate / (1 - decelerationRate);
}

const CarouselCard = ({ imgList, floatImgs }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [index, setIndex] = useState(0);
  const x = useMotionValue(0);
  const count = imgList.length;
  const reducedMotion = useReducedMotionSafe();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setWidth(el.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const goTo = (target, velocity = 0) => {
    const clamped = Math.max(0, Math.min(count - 1, target));
    setIndex(clamped);
    animate(x, -clamped * width, reducedMotion
      ? { duration: 0.15 }
      : { type: "spring", stiffness: 300, damping: 30, velocity }
    );
  };

  const handleDragEnd = (event, info) => {
    if (!width) return goTo(index);
    const projectedOffset = -index * width + project(info.velocity.x);
    goTo(Math.round(-projectedOffset / width), info.velocity.x);
  };

  return (
    <section className="mx-auto max-w-3xl border-t border-ink-800 px-6 py-12" id="skills">
      <h2 className="mb-6 text-center text-2xl font-semibold text-mist-100">Images</h2>

      <div ref={containerRef} className="relative overflow-hidden rounded-lg border border-ink-800">
        <motion.div
          className="flex cursor-grab active:cursor-grabbing"
          style={{ x }}
          drag="x"
          dragConstraints={{ left: -(count - 1) * width, right: 0 }}
          dragElastic={reducedMotion ? 0 : 0.12}
          onDragEnd={handleDragEnd}
        >
          {imgList.map((imgsrc, i) => (
            <div key={i} className="w-full shrink-0">
              <img
                src={imgsrc}
                alt={`Slide ${i + 1} of ${count}`}
                draggable={false}
                className={`h-80 w-full select-none object-cover ${floatImgs ? "shadow-[0_12px_30px_-10px_rgba(0,0,0,0.6)]" : ""}`}
              />
            </div>
          ))}
        </motion.div>

        {count > 1 && (
          <>
            <button
              onClick={() => goTo(index - 1)}
              aria-label="Previous image"
              className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-ink-950/70 p-2 text-mist-100 backdrop-blur-sm"
            >
              <FaChevronLeft size={14} />
            </button>
            <button
              onClick={() => goTo(index + 1)}
              aria-label="Next image"
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-ink-950/70 p-2 text-mist-100 backdrop-blur-sm"
            >
              <FaChevronRight size={14} />
            </button>
          </>
        )}
      </div>

      {count > 1 && (
        <div className="mt-4 flex justify-center gap-2">
          {imgList.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Go to image ${i + 1}`}
              className={`h-2 w-2 rounded-full transition-colors ${i === index ? "bg-signal-500" : "bg-ink-800"}`}
            />
          ))}
        </div>
      )}
    </section>
  )
}

export default CarouselCard
